import type { GameState, Participant } from './types';
import { LAYOUT_PREVIEW_SESSION_ID, type LayoutPreviewValue } from './layoutPreviewContext';

/** Il partecipante «io» nelle view layout preview (slot 1). */
export const PARTICIPANT_LAYOUT_PREVIEW_ME_ID = 'cccccccc-cccc-4ccc-8ccc-cccccccccccc';

const COL = ['#BEFA4F', '#E83399', '#5AA7B9', '#F5B700'] as const;

function nowIso() {
  return new Date().toISOString();
}
function agoSec(s: number) {
  return new Date(Date.now() - s * 1000).toISOString();
}

function baseState(partial: Partial<GameState> & Pick<GameState, 'status' | 'expected_participants'>): GameState {
  return {
    session_id: LAYOUT_PREVIEW_SESSION_ID,
    round: 7,
    theme: 'CIBORGHI E LASAGNE NEL METAVER',
    duration_sec: 300,
    start_time: null,
    voting_duration_sec: 120,
    voting_start_time: null,
    generation_finished_at: null,
    updated_at: nowIso(),
    ...partial,
  };
}

function mkP(
  slot: number,
  o: Partial<Participant> & Pick<Participant, 'id' | 'name' | 'prompt' | 'image_url' | 'votes'>,
): Participant {
  return {
    session_id: LAYOUT_PREVIEW_SESSION_ID,
    slot,
    color: COL[(slot - 1) % 4]!,
    joined_at: agoSec(3600),
    last_seen_at: nowIso(),
    ...o,
  };
}

function participants2(writing: boolean): Participant[] {
  const p1 = mkP(1, {
    id: PARTICIPANT_LAYOUT_PREVIEW_ME_ID,
    name: 'Lime',
    prompt: writing
      ? 'Un ciborgho che impasta lasagne holografiche nel metaverso, stile vaporwave anni 80.'
      : '',
    image_url: null,
    votes: 0,
  });
  const p2 = mkP(2, {
    id: 'dddddddd-dddd-4ddd-8ddd-000000000002',
    name: 'Magenta',
    prompt: writing ? 'Solo silenzio e neon rosa.' : '',
    image_url: null,
    votes: 0,
  });
  return [p1, p2];
}

function participants4(writing: boolean): Participant[] {
  const two = participants2(writing);
  const p3 = mkP(3, {
    id: 'eeeeeeee-eeee-4eee-8eee-000000000003',
    name: 'Teal',
    prompt: writing ? 'Teal vs grid, glitch leggero.' : '',
    image_url: null,
    votes: 0,
  });
  const p4 = mkP(4, {
    id: 'ffffffff-ffff-4fff-8fff-000000000004',
    name: 'Amber',
    prompt: writing ? '—' : '',
    image_url: null,
    votes: 0,
  });
  return [...two, p3, p4];
}

/** Immagine locale 3:2 (`public/test-img.jpeg`) per preview maxi, voto pubblico e partecipante. */
const LAYOUT_PREVIEW_GEN_IMAGE = '/test-img.jpeg';

function withPlaceholderImages32(ps: Participant[]): Participant[] {
  return ps.map((p) => ({
    ...p,
    image_url: LAYOUT_PREVIEW_GEN_IMAGE,
  }));
}

function writingState(exp: 2 | 4, ps: Participant[]): LayoutPreviewValue {
  return {
    state: baseState({
      status: 'WRITING',
      expected_participants: exp,
      start_time: agoSec(95),
      duration_sec: 300,
    }),
    participants: ps,
  };
}

export function buildLayoutPreviewPreset(presetId: string): LayoutPreviewValue | null {
  switch (presetId) {
    case 'slots-writing-2':
      return writingState(2, participants2(true));
    case 'slots-writing-4':
      return writingState(4, participants4(true));

    case 'slots-generating-2': {
      const ps = participants2(false).map((p) => ({ ...p, prompt: p.prompt || 'Prompt salvato.' }));
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 2,
          generation_finished_at: null,
        }),
        participants: ps,
      };
    }
    case 'slots-generating-4': {
      const ps = participants4(false).map((p) => ({ ...p, prompt: p.prompt || 'Ok.' }));
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 4,
          generation_finished_at: null,
        }),
        participants: ps,
      };
    }

    case 'slots-generated-2':
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 2,
          generation_finished_at: agoSec(8),
        }),
        participants: withPlaceholderImages32(
          participants2(false).map((p) => ({ ...p, prompt: 'Output pronto.' })),
        ),
      };
    case 'slots-generated-4':
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 4,
          generation_finished_at: agoSec(8),
        }),
        participants: withPlaceholderImages32(
          participants4(false).map((p) => ({ ...p, prompt: 'Fatto.' })),
        ),
      };

    case 'slots-voting-2': {
      const ps = withPlaceholderImages32(
        participants2(false).map((p) => ({ ...p, prompt: 'Vota!' })),
      ).map((p, i) => ({
        ...p,
        votes: i === 0 ? 12 : 7,
      }));
      return {
        state: baseState({
          status: 'VOTING',
          expected_participants: 2,
          voting_start_time: agoSec(40),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }
    case 'slots-voting-4': {
      const ps = withPlaceholderImages32(
        participants4(false).map((p) => ({ ...p, prompt: 'Vota!' })),
      ).map((p, i) => ({
        ...p,
        votes: [9, 4, 14, 3][i] ?? 0,
      }));
      return {
        state: baseState({
          status: 'VOTING',
          expected_participants: 4,
          voting_start_time: agoSec(25),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }

    case 'slots-winner-2': {
      const ps = withPlaceholderImages32(participants2(false)).map((p, i) => ({
        ...p,
        votes: i === 0 ? 18 : 6,
      }));
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 2,
          voting_start_time: agoSec(400),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }
    case 'slots-winner-4': {
      const ps = withPlaceholderImages32(participants4(false)).map((p, i) => ({
        ...p,
        votes: [11, 22, 7, 15][i] ?? 0,
      }));
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(400),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }

    case 'slots-nowinner-2':
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 2,
          voting_start_time: agoSec(200),
          voting_duration_sec: 120,
        }),
        participants: withPlaceholderImages32(participants2(false)).map((p) => ({ ...p, votes: 0 })),
      };
    case 'slots-nowinner-4':
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(200),
          voting_duration_sec: 120,
        }),
        participants: withPlaceholderImages32(participants4(false)).map((p) => ({ ...p, votes: 0 })),
      };

    case 'slots-nowinner-parity-2':
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 2,
          voting_start_time: agoSec(200),
          voting_duration_sec: 120,
        }),
        participants: withPlaceholderImages32(participants2(false)).map((p) => ({ ...p, votes: 9 })),
      };
    case 'slots-nowinner-parity-4':
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(200),
          voting_duration_sec: 120,
        }),
        participants: withPlaceholderImages32(participants4(false)).map((p) => ({ ...p, votes: 7 })),
      };

    case 'timer-countdown-on':
      return {
        state: baseState({
          status: 'WRITING',
          expected_participants: 4,
          start_time: agoSec(88),
          duration_sec: 300,
        }),
        participants: participants4(true),
      };
    case 'timer-countdown-off':
      return {
        state: baseState({
          status: 'IDLE',
          expected_participants: 4,
          start_time: null,
          duration_sec: 300,
        }),
        participants: participants4(false),
      };

    case 'theme-countdown':
      return {
        state: baseState({
          status: 'WRITING',
          expected_participants: 4,
          start_time: agoSec(40),
          duration_sec: 300,
        }),
        participants: participants4(true),
      };

    case 'participant-waiting':
      return {
        state: baseState({
          status: 'IDLE',
          expected_participants: 4,
        }),
        participants: participants4(false),
      };
    case 'participant-writing':
      return writingState(4, participants4(true));
    case 'participant-generating': {
      const ps = participants4(false).map((p) => ({
        ...p,
        prompt:
          p.prompt ||
          'Un ciborgho che impasta lasagne holografiche nel metaverso, stile vaporwave anni 80.',
      }));
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 4,
          generation_finished_at: null,
        }),
        participants: ps,
      };
    }
    case 'participant-generated-countdown': {
      const ps = withPlaceholderImages32(participants4(false).map((p) => ({ ...p, prompt: 'Output pronto.' })));
      return {
        state: baseState({
          status: 'GENERATING',
          expected_participants: 4,
          generation_finished_at: agoSec(8),
        }),
        participants: ps,
      };
    }
    case 'participant-voting':
      return {
        state: baseState({
          status: 'VOTING',
          expected_participants: 4,
          voting_start_time: agoSec(30),
          voting_duration_sec: 120,
        }),
        participants: withPlaceholderImages32(participants4(false).map((p) => ({ ...p, votes: 3 }))),
      };
    case 'participant-winner': {
      const ps = withPlaceholderImages32(participants4(false)).map((p, i) => ({
        ...p,
        votes: [40, 10, 8, 12][i] ?? 0,
      }));
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(500),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }
    case 'participant-nowinner': {
      const ps = withPlaceholderImages32(participants4(false)).map((p) => ({ ...p, votes: 11 }));
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(500),
          voting_duration_sec: 120,
        }),
        participants: ps,
      };
    }

    case 'vote-not-open': {
      const psVote = withPlaceholderImages32(
        participants4(false).map((p) => ({ ...p, prompt: 'Vota il miglior prompt.' })),
      );
      return {
        state: baseState({ status: 'WAITING_FOR_PLAYERS', expected_participants: 4 }),
        participants: psVote.map((p) => ({ ...p, votes: 0 })),
        votePhase: 'not_open',
      };
    }
    case 'vote-closed': {
      const psVote = withPlaceholderImages32(
        participants4(false).map((p) => ({ ...p, prompt: 'Vota il miglior prompt.' })),
      );
      return {
        state: baseState({
          status: 'ENDED',
          expected_participants: 4,
          voting_start_time: agoSec(999),
          voting_duration_sec: 120,
        }),
        participants: psVote.map((p, i) => ({ ...p, votes: [10, 20, 5, 8][i] ?? 0 })),
        votePhase: 'closed',
      };
    }
    case 'vote-active': {
      const psVote = withPlaceholderImages32(
        participants4(false).map((p) => ({ ...p, prompt: 'Vota il miglior prompt.' })),
      );
      const vs = psVote.map((p, i) => ({ ...p, votes: [6, 9, 4, 7][i] ?? 0 }));
      return {
        state: baseState({
          status: 'VOTING',
          expected_participants: 4,
          voting_start_time: agoSec(15),
          voting_duration_sec: 120,
        }),
        participants: vs,
        votePhase: 'active',
      };
    }
    case 'vote-voted': {
      const psVote = withPlaceholderImages32(
        participants4(false).map((p) => ({ ...p, prompt: 'Vota il miglior prompt.' })),
      );
      const vs = psVote.map((p, i) => ({ ...p, votes: [6, 9, 4, 7][i] ?? 0 }));
      return {
        state: baseState({
          status: 'VOTING',
          expected_participants: 4,
          voting_start_time: agoSec(15),
          voting_duration_sec: 120,
        }),
        participants: vs,
        votePhase: 'voted',
      };
    }

    default:
      return null;
  }
}

export function isValidLayoutPreviewPreset(id: string): boolean {
  return buildLayoutPreviewPreset(id) != null || id === 'no-session';
}
